/**
 * tableVisibility.ts — hides the table COLUMNS the reviewer marked as
 * irrelevant, as a pure AST transform run BEFORE segmentation (same shape and
 * the same reasoning as deptRowFilter.ts: the renderer stays untouched, it just
 * receives a table with fewer/marked cells).
 *
 * Why this can't be CSS: with `display: grid` + subgrid there is no column box
 * to hide — every cell is an independent grid item placed by `colStart`, and
 * `colFractions` (derived from PDF bboxes) still budgets width for a hidden
 * column. The column has to be gone from the plan, not from the paint.
 *
 * SPANS ARE THE WHOLE PROBLEM. In the real marker corpus ~24% of tables carry a
 * cell with colspan >= 2, so "delete column 2" is not "drop the 2nd <td> of
 * every row": a cell spanning columns 1-3 must SHRINK to colspan 2, and only a
 * cell lying entirely inside hidden columns may be dropped. Indices are
 * therefore resolved through computeTableLayout (which also accounts for
 * rowspan carry from the rows above) rather than counted per row.
 *
 * Blocks (paragraphs, images, whole tables) are NOT handled here: they need no
 * AST surgery, because the renderer can already drop a whole top-level block by
 * index (`StaticDocument.hiddenBlocks`). Only the inside of a table has to be
 * rewritten.
 *
 * Two modes, matching the feature's two states:
 *   "remove" — advanced mode OFF: hidden columns are gone, the table reflows.
 *   "mark"   — advanced mode ON: nothing is removed, cells lying entirely in a
 *              hidden column get `data-col-hidden` so the renderer can dim them
 *              in place.
 */

import { ElementNode, HastNode } from "./hast";
import { computeTableLayout } from "./tableLayout";

/** Marks a cell that sits entirely in a hidden column (advanced mode). */
export const COL_HIDDEN_ATTR = "data-col-hidden";

/** Hidden 0-based indices, keyed by `tableId`. */
export type HiddenIndexMap = Record<string, number[]>;

/** What the reviewer has hidden inside tables. */
export interface HiddenTableParts {
    /** 0-based LOGICAL column indices (colspan/rowspan resolved). */
    columns?: HiddenIndexMap;
}

export type VisibilityMode = "remove" | "mark";

/**
 * Stable identity for a hideable part across reloads: the source element id plus
 * an ordinal telling apart the several parts one element can expand into.
 *
 * The importer stores one marker block per ImportedDocumentElement, and in the
 * real corpus 686 of 688 table blocks hold exactly one <table> — so the element
 * id alone is the identity in practice. The ordinal suffix covers the rest
 * rather than silently mis-targeting the second table (or block) of an element.
 */
export function partId(elementId: string | undefined, ordinal: number): string {
    const base = elementId ?? "?";
    return ordinal === 0 ? base : `${base}#${ordinal}`;
}

/** Identity of a <table> within its element (ordinal = which table). */
export const tableId = partId;
/** Identity of a top-level block within its element (ordinal = which node). */
export const blockId = partId;

function isElement(n: HastNode): n is ElementNode {
    return n.type === "element";
}

const ROW_CONTAINERS = new Set(["thead", "tbody", "tfoot"]);

/** <tr>s of a table, thead/tbody/tfoot flattened — mirrors the segmenter. */
function tableRows(table: ElementNode): ElementNode[] {
    const out: ElementNode[] = [];
    for (const child of table.children) {
        if (!isElement(child)) continue;
        if (child.tagName === "tr") out.push(child);
        else if (ROW_CONTAINERS.has(child.tagName)) {
            for (const tr of child.children) {
                if (isElement(tr) && tr.tagName === "tr") out.push(tr);
            }
        }
    }
    return out;
}

interface CellPlan {
    cell: ElementNode;
    /** Lies entirely within hidden columns. */
    hidden: boolean;
    /** Column the cell will resolve to once the hidden columns are gone. */
    newCol: number;
    newColSpan: number;
    newRowSpan: number;
}

/**
 * Decide, for every cell, whether it survives and what its spans become — and
 * which source rows are left with nothing to show.
 *
 * A row is dropped when column-hiding left it with no visible column occupied
 * at all — neither by one of its own surviving cells nor by a rowspan carried
 * down from above. Such a row would render as an empty band.
 */
function planTable(table: ElementNode, hiddenCols: Set<number>) {
    const trs = tableRows(table);
    const { placements, colCount } = computeTableLayout(trs);

    // How many hidden columns lie strictly before column c — the left-shift each
    // surviving cell takes once the hidden ones are gone.
    const shift: number[] = new Array(colCount + 1).fill(0);
    for (let c = 1; c <= colCount; c++) {
        shift[c] = shift[c - 1] + (hiddenCols.has(c - 1) ? 1 : 0);
    }
    const shiftAt = (c: number) => shift[Math.min(c, colCount)];

    const rowHasVisible = new Array(trs.length).fill(false);
    // Rows each cell covers, clamped to the table — a rowspan may overshoot the
    // last <tr>, and the rebuilt cell must not claim bands that do not exist.
    const covered = placements.map((p) => {
        const end = Math.min(p.rowIdx + p.rowSpan, trs.length);
        const rows: number[] = [];
        for (let r = p.rowIdx; r < end; r++) rows.push(r);
        return rows;
    });

    const partial = placements.map((p, i) => {
        let hiddenColsCovered = 0;
        for (let c = p.col; c < p.col + p.colSpan; c++) {
            if (hiddenCols.has(c)) hiddenColsCovered++;
        }
        const hidden = hiddenColsCovered === p.colSpan;
        if (!hidden) {
            // The cell keeps a visible column busy for every row it spans, so
            // none of those rows may be dropped for emptiness.
            for (const r of covered[i]) rowHasVisible[r] = true;
        }
        return { hidden, newColSpan: p.colSpan - hiddenColsCovered };
    });

    const rowSurvives = trs.map((_, r) => rowHasVisible[r]);

    const plans = new Map<ElementNode, CellPlan>();
    placements.forEach((p, i) => {
        plans.set(p.cell, {
            cell: p.cell,
            ...partial[i],
            newCol: p.col - shiftAt(p.col),
            // Every row a surviving cell covers survives — the cell itself is
            // what marks that row visible — so this is just the clamped span.
            newRowSpan: Math.max(1, covered[i].length),
        });
    });

    return { trs, plans, rowSurvives, colCount };
}

function withSpans(cell: ElementNode, colSpan: number, rowSpan: number): ElementNode {
    const properties = { ...cell.properties };
    if (colSpan > 1) properties.colspan = String(colSpan);
    else delete properties.colspan;
    if (rowSpan > 1) properties.rowspan = String(rowSpan);
    else delete properties.rowspan;
    return { ...cell, properties };
}

/**
 * Advanced mode: tag the cells that lie entirely in a hidden column and change
 * nothing else, so the reviewer sees what they are about to lose.
 */
function markTable(table: ElementNode, plans: Map<ElementNode, CellPlan>): ElementNode {
    const visit = (node: HastNode): HastNode => {
        if (!isElement(node)) return node;
        const plan = plans.get(node);
        if (plan) {
            if (!plan.hidden) return node;
            return { ...node, properties: { ...node.properties, [COL_HIDDEN_ATTR]: "" } };
        }
        return { ...node, children: node.children.map(visit) };
    };
    return visit(table) as ElementNode;
}

/**
 * Normal mode: re-emit each surviving row with the cells that survive in it.
 *
 * Cells keep their own row and their source order — a column edit never moves a
 * cell between rows, and source order within a <tr> is already column order.
 * Non-cell children of a <tr> (inter-cell whitespace, comments) are not carried
 * over; HTML table layout ignores them and marker emits none.
 */
function rebuildTable(
    table: ElementNode,
    trs: ElementNode[],
    plans: Map<ElementNode, CellPlan>,
    rowSurvives: boolean[],
): { next: ElementNode; expectedCols: number[] } {
    const rowIdxOf = new Map<ElementNode, number>();
    trs.forEach((tr, i) => rowIdxOf.set(tr, i));

    // Planned columns in emission order. computeTableLayout returns placements in
    // row-then-source order, i.e. exactly this order, so verification is a
    // positional compare rather than a node-identity lookup.
    const expectedCols: number[] = [];

    const mapRows = (children: HastNode[]): HastNode[] => {
        const out: HastNode[] = [];
        for (const c of children) {
            if (isElement(c) && c.tagName === "tr") {
                const idx = rowIdxOf.get(c);
                if (idx == null || !rowSurvives[idx]) continue;
                const cells: HastNode[] = [];
                for (const child of c.children) {
                    const plan = isElement(child) ? plans.get(child) : undefined;
                    if (!plan || plan.hidden) continue;
                    expectedCols.push(plan.newCol);
                    cells.push(withSpans(plan.cell, plan.newColSpan, plan.newRowSpan));
                }
                out.push({ ...c, children: cells });
            } else if (isElement(c) && ROW_CONTAINERS.has(c.tagName)) {
                out.push({ ...c, children: mapRows(c.children) });
            } else {
                out.push(c); // caption / colgroup / text
            }
        }
        return out;
    };

    const next = { ...table, children: mapRows(table.children) };
    return { next, expectedCols };
}

/**
 * Re-derive the rebuilt table's layout and check it matches what we planned:
 * the same column count minus the hidden ones, and every surviving cell landing
 * on the column it was placed for.
 *
 * This is a fail-safe for self-contradictory source tables. Marker sometimes
 * emits a rowspan cell that OVERLAPS the next row's own colspan cell (e.g. a
 * `colspan=3 rowspan=2` header above a `colspan=4` row). computeTableLayout
 * resolves the overlap by letting the later cell sit on top; once a part is
 * removed the two disagree differently and the table can come back WIDER than it
 * started. Measured on the real corpus: 8 of 2421 hide operations, all from one
 * pathological table. Rather than render a table that grew empty columns, the
 * caller refuses the edit and leaves the table untouched.
 */
function verifyRebuild(
    next: ElementNode,
    expectedCols: number[],
    colCount: number,
    hiddenCols: Set<number>,
): boolean {
    let removedCols = 0;
    hiddenCols.forEach((c) => {
        if (c < colCount) removedCols++;
    });
    const layout = computeTableLayout(tableRows(next));
    // Upper bound rather than equality: a source table whose rows disagree about
    // their width can come back narrower than the arithmetic suggests, which is
    // harmless. What must never happen is it coming back WIDER — that is the
    // overlapping-span pathology.
    if (layout.colCount > Math.max(0, colCount - removedCols)) return false;
    if (layout.placements.length !== expectedCols.length) return false;
    // The real guarantee: every surviving cell lands on the column it was
    // planned for. Placements come back in row-then-source order, which is the
    // order they were emitted in.
    return layout.placements.every((p, i) => p.col === expectedCols[i]);
}

function transformTable(
    table: ElementNode,
    hiddenCols: Set<number>,
    mode: VisibilityMode,
    onRefused?: () => void,
): ElementNode {
    if (hiddenCols.size === 0) return table;

    const { trs, plans, rowSurvives, colCount } = planTable(table, hiddenCols);

    if (mode === "mark") return markTable(table, plans);

    const { next, expectedCols } = rebuildTable(table, trs, plans, rowSurvives);

    if (!verifyRebuild(next, expectedCols, colCount, hiddenCols)) {
        onRefused?.();
        if (process.env.NODE_ENV !== "production") {
            // eslint-disable-next-line no-console
            console.warn(
                "[tableVisibility] refusing to hide a column of a table: the source has " +
                    "overlapping row/col spans, so the result would not re-derive the " +
                    "planned layout; left unchanged",
            );
        }
        return table;
    }

    return next;
}

/**
 * Apply the reviewer's column choices to the document tree.
 *
 * Top-level node identity and order are preserved (a table stays a table, in
 * place), so a caller's parallel `elementIds` array stays aligned — the same
 * contract deptRowFilter honours.
 */
export function applyTableVisibility(
    nodes: HastNode[],
    elementIds: Array<string | undefined>,
    parts: HiddenTableParts,
    mode: VisibilityMode,
    /**
     * Filled with the ids of tables whose edit was refused (see verifyRebuild).
     * An out-param rather than a second return value so the common call site
     * stays a plain expression; callers that care create the set themselves,
     * inside the same `useMemo`, so nothing leaks across renders.
     */
    refused?: Set<string>,
): HastNode[] {
    const { columns = {} } = parts;
    if (Object.keys(columns).length === 0) return nodes;

    // Ordinal counts tables per ELEMENT, not per node: one element's contentText
    // can parse into several top-level nodes, and a block holding two sibling
    // tables becomes two nodes sharing an element id. Counting per node would
    // give both ordinal 0, so hiding a column in one would hit the other too.
    // The segmenter numbers tables the same way (see the tableIds pre-pass).
    const ordinals = new Map<string | undefined, number>();

    return nodes.map((node, i) => {
        const elementId = elementIds[i];
        const visit = (n: HastNode): HastNode => {
            if (!isElement(n)) return n;
            if (n.tagName === "table") {
                const ordinal = ordinals.get(elementId) ?? 0;
                ordinals.set(elementId, ordinal + 1);
                const id = tableId(elementId, ordinal);
                return transformTable(
                    n,
                    new Set(columns[id]),
                    mode,
                    refused && (() => refused.add(id)),
                );
            }
            return { ...n, children: n.children.map(visit) };
        };
        return visit(node);
    });
}

/**
 * Text of a cell. Not `nodeText`: marker breaks headers with <br>
 * ("Identification<br>number<br>requirement"), and plain concatenation would
 * read "Identificationnumberrequirement" on the toolbar button.
 */
function labelText(node: HastNode): string {
    const parts: string[] = [];
    const walk = (n: HastNode) => {
        if (n.type === "text") parts.push(n.value);
        else if (n.type === "element") {
            if (n.tagName === "br") parts.push(" ");
            else n.children.forEach(walk);
        }
    };
    walk(node);
    return parts.join("").replace(/\s+/g, " ").trim();
}

/**
 * Column labels for the per-table toolbar, taken from the first row that has a
 * <th> (else the first row). A spanning header labels every column it covers,
 * so a 2-column "Requirements" header reads "Requirements" above both.
 * Callers fall back to a positional label for blanks.
 */
export function columnLabels(table: ElementNode): string[] {
    const trs = tableRows(table);
    const { colCount, placements } = computeTableLayout(trs);
    const labels: string[] = new Array(colCount).fill("");
    if (colCount === 0) return labels;

    const headerRowIdx = trs.findIndex((tr) =>
        tr.children.some((c) => isElement(c) && c.tagName === "th"),
    );
    const rowIdx = headerRowIdx >= 0 ? headerRowIdx : 0;

    for (const p of placements) {
        if (p.rowIdx !== rowIdx) continue;
        const text = labelText(p.cell);
        for (let c = p.col; c < Math.min(p.col + p.colSpan, colCount); c++) {
            if (!labels[c]) labels[c] = text;
        }
    }
    return labels;
}

/** Logical column count — how many toolbar buttons a table needs. */
export function columnCount(table: ElementNode): number {
    return computeTableLayout(tableRows(table)).colCount;
}
